import React, { useEffect, useState } from "react";
import { FileText, Download, Calendar, Search, AlertCircle } from "lucide-react";
import PageBanner, { SectionTitle } from "../components/PageBanner";
import { circulars as fallbackCirculars, pageBanners } from "../data/mock";
import { fetchSiteCirculars, DRIVE_NOT_CONNECTED } from "../lib/siteDrive";
// Write actions live on the signed-in dashboard. Public page is read-only.
// import { canEdit } from "../lib/access";

export default function Circulars() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [status, setStatus] = useState("loading");
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const [driveFiles, setDriveFiles] = useState([]);

  useEffect(() => {
    let live = true;
    fetchSiteCirculars().then((d) => {
      if (!live) return;
      setDriveFiles(d.files || []);
      setConnected(d.connected === true);
      setError(d.error || "");
      setStatus(d.connected ? "ready" : "fallback");
    }).catch(() => {
      if (!live) return;
      setError(DRIVE_NOT_CONNECTED);
      setStatus("fallback");
    });
    return () => { live = false; };
  }, []);

  const source = connected ? driveFiles : fallbackCirculars;
  const types = ["All", ...Array.from(new Set(source.map((c) => c.type).filter(Boolean)))];
  const filtered = source.filter((c) =>
    (type === "All" || c.type === type) &&
    ((c.title || "").toLowerCase().includes(q.toLowerCase()) || (c.gono || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="page-enter">
      <PageBanner image={pageBanners.circulars} title="Circulars & Government Orders" crumb="Circular & GOs" subtitle="Official circulars, government orders and notifications issued by the Corporation." />
      <section style={{ padding: "50px 24px 10px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <SectionTitle>Notifications &amp; Orders</SectionTitle>
          {status === "fallback" && (
            <div data-testid="circulars-drive-error" style={{ marginBottom: 16, padding: "12px 14px", background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.28)", borderRadius: 12, color: "#B91C1C", fontSize: 13.5, display: "flex", gap: 8 }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              {error || DRIVE_NOT_CONNECTED}. Showing the notices already on this page until Drive is connected on the server.
            </div>
          )}
          <div className="filter-bar" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#fff", padding: "6px 6px 6px 16px", borderRadius: 12, border: "1.5px solid rgba(249,115,22,0.25)", boxShadow: "0 4px 14px -6px rgba(194,65,12,0.14)" }}>
              <Search size={18} color="#F97316" />
              <input data-testid="circular-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search circulars or G.O. numbers..." style={{ flex: 1, border: "none", outline: "none", padding: "10px 0", fontSize: 14, background: "transparent", color: "#1C1917", fontFamily: "inherit" }} />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {types.map(t => (
                <button key={t} data-testid={`circular-filter-${t.toLowerCase()}`} onClick={() => setType(t)} className={type === t ? "btn-primary-3d" : "btn-outline-3d"} style={{ padding: "8px 14px", fontSize: 12 }}>{t}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: "30px 24px 60px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gap: 14 }} data-testid="circulars-list">
          {status === "loading" && (
            <div data-testid="circulars-loading" style={{ textAlign: "center", padding: 60, color: "#78716C" }}>Loading circulars…</div>
          )}
          {status !== "loading" && filtered.map(c => (
            <div key={c.id} data-testid={`circular-${c.id}`} className="premium-card circ-row" style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 18, alignItems: "center", padding: 20, borderLeft: `5px solid ${c.type === "Notification" ? "#DC2626" : c.type === "Order" ? "#FACC15" : "#F97316"}` }}>
              <div className={`icon-tile ${c.type === "Notification" ? "icon-tile-red" : c.type === "Order" ? "icon-tile-yellow" : ""}`}>
                <FileText size={20} />
              </div>
              <div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span className={`badge ${c.type === "Notification" ? "badge-red" : c.type === "Order" ? "badge-yellow" : "badge-brown"}`}>{c.type}</span>
                  {c.gono && <span style={{ fontSize: 12, color: "#78716C", fontWeight: 600 }}>{c.gono}</span>}
                  <span style={{ fontSize: 12, color: "#78716C" }}><Calendar size={11} style={{ verticalAlign: "middle" }} /> {c.date}</span>
                </div>
                <div className="font-serif" style={{ fontSize: 17, color: "#1C1917", fontWeight: 700, marginTop: 8 }}>{c.title}</div>
              </div>
              <a href={c.url || c.view || c.download} target="_blank" rel="noreferrer" data-testid={`circular-download-${c.id}`} className="btn-outline-3d" style={{ padding: "10px 14px", fontSize: 13, textDecoration: "none", display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}><Download size={14} style={{ marginRight: 6 }} />View / Download</a>
            </div>
          ))}
          {status !== "loading" && filtered.length === 0 && (
            <div data-testid="circulars-empty" style={{ textAlign: "center", padding: 60, color: "#78716C" }}>
              <FileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>{connected && !q && type === "All" ? "No circulars have been published yet." : "No circulars match your search"}</div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
